import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemContent,
  ListDivider,
  Sheet,
  Table,
  iconButtonClasses,
  Button,
  IconButton,
  Card,
  Input,
  ButtonGroup,
  Stack,
} from "@mui/joy";
import { toast } from "react-toastify";
import {
  LoadingView,
  PageTitle,
  DatePickerModal,
  formatRangeLabel,
  StatusBadge,
} from "../../../components";
import { thousandSeparator } from "../../../utils";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {
  RefreshOutlined,
  RemoveRedEyeOutlined,
  WarningRounded,
  CalendarMonthOutlined,
} from "@mui/icons-material";
import {
  fetchMerchantTransactionsRequest,
  fetchMerchantTransactionsReset,
} from "../../../store/actions";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { STATUS, getMerchantTransTypeLabel } from "../../../constant";
import { NAVIGATE_TO_MERCHANTTRANSACTIONDETAILSPAGE } from "../../../route/types";

const getStatusChipColor = (status: string) => {
  if (status === "SUCCESS") return "success";
  if (status === "FAILED" || status === "ERROR") return "danger";
  if (
    status === "PENDING" ||
    status === "PROCESSING" ||
    status === "INITIATED"
  )
    return "warning";
  return "neutral";
};

const MobileViewTable = ({ data, props }) => {
  return (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      {data.map((listItem, index) => (
        <List key={index} size="sm" sx={{ "--ListItem-paddingX": 0 }}>
          <ListItem
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <ListItemContent
              sx={{ display: "flex", gap: 2, alignItems: "start" }}
            >
              <div>
                <Typography fontWeight={600} gutterBottom>
                  {thousandSeparator(listItem.amount)} {listItem.currency}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Payer: </b> {listItem.payer_name}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Customer Ref: </b> {listItem.customer_reference}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Trans Type: </b>{" "}
                  {getMerchantTransTypeLabel(listItem.trans_type)}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Date: </b> {listItem.trans_date}
                </Typography>
              </div>
            </ListItemContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-end",
                rowGap: 1,
              }}
            >
              <StatusBadge
                color={getStatusChipColor(listItem.status)}
              >
                {listItem.status}
              </StatusBadge>
              <ButtonGroup variant="soft" size="sm">
                <Button color="primary" onClick={() => props.view(listItem)}>
                  <RemoveRedEyeOutlined />
                </Button>
              </ButtonGroup>
            </Box>
          </ListItem>
          <ListDivider />
        </List>
      ))}
    </Box>
  );
};

const DesktopViewTable = ({ data, props }) => {
  return (
    <Sheet
      variant="outlined"
      sx={{
        display: { xs: "none", md: "flex" },
        borderRadius: "sm",
        flexShrink: 1,
        overflow: "auto",
        minHeight: 0,
      }}
    >
      <Table
        aria-labelledby="tableTitle"
        stickyHeader
        hoverRow
        sx={{
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
          "--Table-headerUnderlineThickness": "1px",
          "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
          "--TableCell-paddingY": "4px",
          "--TableCell-paddingX": "8px",
          "& tr > *:last-child": {
            position: "sticky",
            right: 0,
            bgcolor: "var(--TableCell-headBackground)",
          },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 110, padding: "10px 6px" }}>Amount</th>
            <th style={{ width: 130, padding: "10px 6px" }}>Customer Ref</th>
            <th style={{ width: 140, padding: "10px 6px" }}>
              OpenPesa Reference
            </th>
            <th style={{ width: 130, padding: "10px 6px" }}>Payer Name</th>
            <th style={{ width: 90, padding: "10px 6px" }}>Status</th>
            <th style={{ width: 130, padding: "10px 6px" }}>Trans Date</th>
            <th style={{ width: 150, padding: "10px 6px" }}>Trans Type</th>
            <th aria-label="last" style={{ width: 50, padding: "10px 6px" }} />
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td>
                <Typography level="body-sm">
                  {thousandSeparator(row.amount)} {row.currency}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
                  {row.customer_reference}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
                  {row.openpesa_reference}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
                  {row.payer_name}
                </Typography>
              </td>
              <td>
                <StatusBadge
                  color={getStatusChipColor(row.status)}
                >
                  {row.status}
                </StatusBadge>
              </td>
              <td>
                <Typography level="body-sm">{row.trans_date}</Typography>
              </td>
              <td>
                <Typography level="body-sm" sx={{ whiteSpace: "nowrap" }}>
                  {getMerchantTransTypeLabel(row.trans_type)}
                </Typography>
              </td>
              <td>
                <ButtonGroup variant="soft" size="sm">
                  <Button
                    title="View"
                    color="primary"
                    onClick={() => props.view(row)}
                  >
                    <RemoveRedEyeOutlined />
                  </Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

const MerchantTransactionsPage = ({
  loginResult,
  merchantTransactionsStatus,
  merchantTransactionsResult,
  merchantTransactionsErrorMessage,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const merchant_code = state?.merchant_code;
  const merchant_name = state?.merchant_name || "";
  const isDesktop = useMediaQuery("(min-width:600px)");

  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 20;

  const fetchTransactions = (start = "", end = "") => {
    if (merchant_code == null || merchant_code === "") return;
    dispatch(
      fetchMerchantTransactionsRequest(loginResult.token, {
        code: merchant_code,
        start_date: start || null,
        end_date: end || null,
      }),
    );
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (merchantTransactionsStatus === STATUS.ERROR) {
      toast.error(merchantTransactionsErrorMessage);
      dispatch(fetchMerchantTransactionsReset());
    }
  }, [merchantTransactionsStatus, merchantTransactionsErrorMessage, dispatch]);

  const transData = merchantTransactionsResult || [];

  const filteredData = transData.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      item.customer_reference?.toLowerCase().includes(q) ||
      item.openpesa_reference?.toLowerCase().includes(q) ||
      item.payer_name?.toLowerCase().includes(q) ||
      item.currency?.toLowerCase().includes(q) ||
      item.status?.toLowerCase().includes(q) ||
      getMerchantTransTypeLabel(item.trans_type).toLowerCase().includes(q) ||
      item.trans_date?.includes(search) ||
      item.amount?.toString().includes(search) ||
      item.merchant_code?.toString().includes(search)
    );
  });

  const pageLength = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const viewTransactionDetails = (details) => {
    navigate(NAVIGATE_TO_MERCHANTTRANSACTIONDETAILSPAGE, {
      state: { transaction_id: details.id, merchant_code },
    });
  };

  const applyDateFilter = () => {
    setCurrentPage(0);
    fetchTransactions(startDate, endDate);
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
    fetchTransactions("", "");
  };

  const handleSelectStart = (value) => {
    setStartDate(value);
    setCurrentPage(0);
  };

  const handleSelectEnd = (value) => {
    setEndDate(value);
    setCurrentPage(0);
  };

  return (
    <Box>
      <PageTitle
        title={
          merchant_name
            ? `${merchant_name} Transactions`
            : "Merchant Transactions"
        }
      />
      <LoadingView loading={merchantTransactionsStatus === STATUS.LOADING} />

      <Sheet
        sx={{
          display: "flex",
          my: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { sm: "center" },
        }}
      >
        <Box
          sx={{
            gap: 1,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <Stack spacing={0.5}>
            <Typography level="body-xs">Start date</Typography>
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              startDecorator={<CalendarMonthOutlined />}
              onClick={() => setStartOpen(true)}
              sx={{ justifyContent: "flex-start", minWidth: 170 }}
            >
              {startDate ? formatRangeLabel(startDate) : "Select start"}
            </Button>
          </Stack>
          <Stack spacing={0.5}>
            <Typography level="body-xs">End date</Typography>
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              startDecorator={<CalendarMonthOutlined />}
              onClick={() => setEndOpen(true)}
              sx={{ justifyContent: "flex-start", minWidth: 170 }}
            >
              {endDate ? formatRangeLabel(endDate) : "Select end"}
            </Button>
          </Stack>
          <Button
            size="sm"
            onClick={applyDateFilter}
            disabled={!startDate && !endDate}
          >
            APPLY
          </Button>
          {(startDate || endDate) && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={clearDateFilter}
            >
              CLEAR
            </Button>
          )}
        </Box>

        {transData.length > 0 && (
          <Input
            size="sm"
            placeholder="Search transactions..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(0);
            }}
            startDecorator={<SearchIcon />}
            sx={{ width: { xs: "auto", md: "30%" } }}
          />
        )}
      </Sheet>

      <DatePickerModal
        open={startOpen}
        value={startDate}
        onSelect={handleSelectStart}
        onClose={() => setStartOpen(false)}
      />
      <DatePickerModal
        open={endOpen}
        value={endDate}
        onSelect={handleSelectEnd}
        onClose={() => setEndOpen(false)}
      />

      {transData.length > 0 ? (
        <>
          <MobileViewTable
            data={paginatedData}
            props={{ view: viewTransactionDetails }}
          />
          <DesktopViewTable
            data={paginatedData}
            props={{ view: viewTransactionDetails }}
          />

          {filteredData.length > ITEMS_PER_PAGE && (
            <Box
              sx={{
                pt: 2,
                gap: 1,
                [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
                display: "flex",
              }}
            >
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<KeyboardArrowLeftIcon />}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                {isDesktop ? "Previous" : ""}
              </Button>

              <Box sx={{ flex: 1 }} />

              {Array.from({ length: pageLength }).map((_, page) => (
                <IconButton
                  key={page}
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  onClick={() => setCurrentPage(page)}
                  disabled={currentPage === page}
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  {page + 1}
                </IconButton>
              ))}

              <Typography
                level="body-sm"
                mx="auto"
                textAlign="center"
                sx={{ display: { xs: "flex", md: "none" } }}
              >
                Page {currentPage + 1} of {pageLength}
              </Typography>

              <Box sx={{ flex: 1 }} />

              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                endDecorator={<KeyboardArrowRightIcon />}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, pageLength - 1))
                }
                disabled={currentPage === pageLength - 1}
              >
                {isDesktop ? "Next" : ""}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            top: "20%",
          }}
        >
          <WarningRounded
            sx={{ fontSize: "50px", color: "background.appcolor" }}
          />
          <Typography level="title-md">Resource not Found</Typography>
          <Typography level="body-sm" textAlign="center">
            No transactions found for this merchant or a network issue
            occurred.
          </Typography>
          <Button
            onClick={() => fetchTransactions(startDate, endDate)}
            startDecorator={<RefreshOutlined />}
          >
            REFRESH
          </Button>
        </Card>
      )}
    </Box>
  );
};

const mapStateToProps = ({ merchant, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    merchantTransactionsStatus,
    merchantTransactionsResult,
    merchantTransactionsErrorMessage,
  } = merchant;
  return {
    loginResult,
    merchantTransactionsStatus,
    merchantTransactionsResult,
    merchantTransactionsErrorMessage,
  };
};

export default connect(mapStateToProps, {})(MerchantTransactionsPage);
